import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { KeyRound, ArrowLeft, CheckCircle, XCircle, RotateCcw } from 'lucide-react';
import './ForgotPassword.css';

const STEP = { INPUT: 1, OTP: 2 };

const ForgotPassword = () => {
  const [step, setStep] = useState(STEP.INPUT);
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const [codes, setCodes] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const inputRefs = useRef([]);
  const { forgotPassword, verifyResetOtp } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (step !== STEP.OTP) return;
    if (countdown <= 0) { setCanResend(true); return; }
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, step]);

  React.useEffect(() => {
    if (step === STEP.OTP) {
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [step]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!emailOrPhone.trim()) {
      setError('Please enter your email or phone number');
      return;
    }

    setLoading(true);
    try {
      const data = await forgotPassword(emailOrPhone.trim());
      setMaskedPhone(data.maskedPhone || '');
      setSuccess(data.message);
      setStep(STEP.OTP);
      setCountdown(60);
      setCanResend(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCodes = [...codes];
    newCodes[index] = value.slice(-1);
    setCodes(newCodes);
    setError('');
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
    if (newCodes.every(c => c !== '') && value) handleVerifyOtp(newCodes.join(''));
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codes[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setCodes(pasted.split(''));
      inputRefs.current[5]?.focus();
      handleVerifyOtp(pasted);
    }
  };

  const handleVerifyOtp = async (codeStr) => {
    const finalCode = codeStr || codes.join('');
    if (finalCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const data = await verifyResetOtp(emailOrPhone.trim(), finalCode);
      navigate('/reset-password', {
        state: { email: data.email, verified: true }
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired code');
      setCodes(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const data = await forgotPassword(emailOrPhone.trim());
      setSuccess('New reset code sent!');
      setCountdown(60);
      setCanResend(false);
      setCodes(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fp-container">
      <div className="fp-bg">
        <div className="fp-bg-gradient"></div>
      </div>

      <div className="fp-card">

        {step === STEP.INPUT && (
          <>
            <div className="fp-icon">
              <KeyRound size={28} />
            </div>
            <h2 className="fp-title">Forgot Password?</h2>
            <p className="fp-subtitle">
              Enter your email or phone number and we'll send you a reset code.
            </p>

            {error && (
              <div className="fp-alert-error">
                <XCircle size={15} />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSendOtp} className="fp-form">
              <div className="fp-input-group">
                <label className="fp-label">Email or Phone Number</label>
                <input
                  type="text"
                  className="fp-input"
                  placeholder="you@company.com or +255XXXXXXXXX"
                  value={emailOrPhone}
                  onChange={(e) => { setEmailOrPhone(e.target.value); setError(''); }}
                  required
                  autoFocus
                />
              </div>

              <button type="submit" className="fp-btn" disabled={loading}>
                {loading ? (
                  <span className="fp-btn-loading">
                    <span className="fp-spinner"></span>
                    Sending...
                  </span>
                ) : 'Send Reset Code'}
              </button>
            </form>

            <div className="fp-back">
              <Link to="/login">
                <ArrowLeft size={15} />
                Back to Login
              </Link>
            </div>
          </>
        )}

        {step === STEP.OTP && (
          <>
            <div className="fp-icon">
              <KeyRound size={28} />
            </div>
            <h2 className="fp-title">Enter Reset Code</h2>
            <p className="fp-subtitle">
              We sent a 6-digit code to <strong>{maskedPhone}</strong>
            </p>

            {success && (
              <div className="fp-alert-success">
                <CheckCircle size={15} />
                <span>{success}</span>
              </div>
            )}

            {error && (
              <div className="fp-alert-error">
                <XCircle size={15} />
                <span>{error}</span>
              </div>
            )}

            <div className="fp-otp-inputs" onPaste={handleOtpPaste}>
              {codes.map((digit, index) => (
                <input
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  className={`fp-otp-input ${digit ? 'filled' : ''} ${error ? 'otp-error' : ''}`}
                  value={digit}
                  onChange={e => handleOtpChange(index, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(index, e)}
                  disabled={loading}
                />
              ))}
            </div>

            <button
              className="fp-btn"
              onClick={() => handleVerifyOtp()}
              disabled={loading || codes.some(c => c === '')}
            >
              {loading ? (
                <span className="fp-btn-loading">
                  <span className="fp-spinner"></span>
                  Verifying...
                </span>
              ) : 'Verify Code'}
            </button>

            <div className="fp-resend">
              {canResend ? (
                <button className="fp-resend-btn" onClick={handleResend} disabled={loading}>
                  <RotateCcw size={14} />
                  {loading ? 'Sending...' : 'Resend Code'}
                </button>
              ) : (
                <p className="fp-timer">Resend code in <strong>{countdown}s</strong></p>
              )}
            </div>

            <div className="fp-back">
              <button onClick={() => { setStep(STEP.INPUT); setError(''); setSuccess(''); setCodes(['','','','','','']); }}>
                <ArrowLeft size={15} />
                Change email/phone
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;